import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileAddOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  TranslationOutlined,
} from '@ant-design/icons';
import { PERMISSIONS } from '@/constants';
import { cn } from '@/libs/utils';
import { useAuthStore } from '@/store/useAuthStore';
import { hasRequiredActionsPermissions } from '@/utils/authUtils';
import CompanyCustomDocsForm from '../../_components/form/CompanyCustomDocsForm';
import FunctionaryOwnershipForm from '../../_components/form/FunctionaryOwnershipForm';
import StatuaryDocsForm from '../../_components/form/StatuaryDocsForm';
import UploadSalaryTranslationForm from '../../_components/form/UploadSalaryTranslationForm';

/**
 * "Company documents" tile: the document tools as cards on the left, the
 * chosen one on the right. Same forms (and permissions) as the old page's
 * create / upload documents tabs, without their own card and title.
 */
export default function CompanyDocumentsPanel({ companyId, formData, access }) {
  const { t } = useTranslation(['forms']);
  const permissions = useAuthStore((state) => state.user?.permissions);
  // HLB's own AML / authorisation documents (see CompanyCreateDocumentsForm).
  const canCreateStatuaryDocs = useMemo(
    () =>
      hasRequiredActionsPermissions(permissions || [], [
        PERMISSIONS.companyStatuaryDocumentsCreate,
      ]),
    [permissions]
  );

  const tools = [
    access.createDocuments && {
      id: 'custom',
      icon: <FileAddOutlined />,
      title: t('companyDocuments', 'Kompanijski dokumenti'),
      desc: t(
        'companyDocumentsHint',
        'Interna i eksterna poslovna dokumentacija — ne odnosi se na zaposlene.'
      ),
      render: () => <CompanyCustomDocsForm bare companyId={companyId} />,
    },
    access.createDocuments &&
      canCreateStatuaryDocs && {
        id: 'statuary',
        icon: <SafetyCertificateOutlined />,
        title: t('statuary_documents.title'),
        desc: 'PEP · CROSO · PoA',
        render: () => (
          <StatuaryDocsForm bare companyId={companyId} companyData={formData} />
        ),
      },
    access.createDocuments &&
      canCreateStatuaryDocs && {
        id: 'functionary',
        icon: <TeamOutlined />,
        title: t('functionary_ownership.title'),
        desc: 'SPNFT 1 · 2 · 3',
        render: () => (
          <FunctionaryOwnershipForm
            bare
            companyId={companyId}
            companyData={formData}
          />
        ),
      },
    access.uploadDocuments && {
      id: 'translation',
      icon: <TranslationOutlined />,
      title: t('salary_translation.title'),
      desc: t('salary_translation.description'),
      render: () => <UploadSalaryTranslationForm bare />,
    },
  ].filter(Boolean);

  const [selectedId, setSelectedId] = useState(null);
  const selected = tools.find((tool) => tool.id === selectedId) || tools[0];
  if (!selected) return null;

  return (
    <div className="grid grid-cols-[260px_minmax(0,1fr)] gap-4 pt-3">
      <nav className="flex flex-col gap-2">
        {tools.map((tool) => {
          const on = tool.id === selected.id;
          return (
            <button
              key={tool.id}
              type="button"
              aria-pressed={on}
              onClick={() => setSelectedId(tool.id)}
              className={cn(
                'flex items-start gap-2.5 rounded-xl border p-2.5 text-left transition-colors',
                on
                  ? 'border-[rgba(var(--accentColor),0.45)] bg-[rgba(var(--accentColor),0.07)]'
                  : 'border-zinc-200 bg-white hover:border-accent'
              )}
            >
              <span
                className={cn(
                  'grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[15px]',
                  on
                    ? 'bg-[rgba(var(--accentColor),0.14)] text-accent'
                    : 'bg-zinc-100 text-zinc-500'
                )}
              >
                {tool.icon}
              </span>
              <span className="min-w-0">
                <span className="block text-[12.5px] font-bold leading-tight text-zinc-900">
                  {tool.title}
                </span>
                <span className="mt-0.5 line-clamp-2 block text-[11.5px] leading-snug text-zinc-500">
                  {tool.desc}
                </span>
              </span>
            </button>
          );
        })}
      </nav>

      <section className="min-w-0 rounded-xl border border-zinc-200 bg-white p-4">
        <header className="mb-4 flex items-start gap-2.5 border-b border-zinc-100 pb-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[rgba(var(--accentColor),0.12)] text-base text-accent">
            {selected.icon}
          </span>
          <span>
            <span className="block text-[15px] font-extrabold text-zinc-900">
              {selected.title}
            </span>
            <span className="block text-[12px] text-zinc-500">
              {selected.desc}
            </span>
          </span>
        </header>
        {selected.render()}
      </section>
    </div>
  );
}
